import { ArrowDownIcon, Expand05Icon, MinimizeIcon } from 'assets/icons';
import { adaptContentType, classnames } from 'helpers/utils';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { useEffect, useRef, useState } from 'react';
import { AlbumContentType } from 'common/enums';
import { Menu, PositionType } from 'common/menu';
import { DropDownListItem } from 'common/drop-down-list-item';
import { useClickOutside } from 'hooks/click-outside';
import styles from './controls.module.scss';

// Whether a key event comes from an element the user is typing into.
const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  );
};

const HIDE_DELAY_MS = 2000;
interface ControlsProps {
  isFullScreen: boolean;
  setIsFullScreen: (isFullScreen: boolean) => void;
  prevContent: () => void;
  nextContent: () => void;
  currentIndex: number;
  allContents: AlbumContentSerialized[];
  currentContent: AlbumContentSerialized;
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  autoAdvance: boolean;
  setAutoAdvance: (autoAdvance: boolean) => void;
  autoAdvanceCheckedOptions: {
    [key in AlbumContentType]: boolean;
  };
  handleAutoAdvanceCheckedOptionChange: (contentType: AlbumContentType) => void;
}

const getAutoplayDuration = (contentType: AlbumContentType): number => {
  switch (contentType) {
    case AlbumContentType.image:
      return 8000;
    case AlbumContentType.write:
    case AlbumContentType.document:
      return 15000;
    default:
      return 0;
  }
};

export const Controls = ({
  isFullScreen,
  setIsFullScreen,
  prevContent,
  nextContent,
  currentIndex,
  allContents,
  currentContent,
  isPlaying,
  setIsPlaying,
  autoAdvance,
  setAutoAdvance,
  autoAdvanceCheckedOptions,
  handleAutoAdvanceCheckedOptionChange,
}: ControlsProps) => {
  const hideAutoAdvance = allContents.length === 1;
  const [controlsVisible, setControlsVisible] = useState(true);
  const [showAutoAdvanceSettings, setShowAutoAdvanceSettings] = useState(false);
  const hideTimer = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef(0);
  const menuRef = useRef<HTMLDivElement>(null);
  useClickOutside(menuRef, () => setShowAutoAdvanceSettings(false));

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === allContents.length - 1;

  // Turning auto-advance on also opens its settings (which content types to
  // include); turning it off closes them. There is no separate gear button.
  const toggleAutoAdvance = () => {
    const next = !autoAdvance;
    setAutoAdvance(next);
    setIsPlaying(next);
    setShowAutoAdvanceSettings(next);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    // Player shortcuts must not hijack typing: the description editor, the
    // inline title rename, etc. live on the same screen, and preventDefault on
    // space would otherwise swallow every whitespace typed into them.
    if (isTypingTarget(event.target)) return;
    switch (event.key) {
      case ' ':
        event.preventDefault();
        setIsPlaying(!isPlaying);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        prevContent();
        break;
      case 'ArrowRight':
        event.preventDefault();
        nextContent();
        break;
      case 'Escape':
        if (isFullScreen) {
          event.preventDefault();
          setIsFullScreen(false);
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevContent, nextContent, isPlaying, isFullScreen, setIsFullScreen]);

  useEffect(() => {
    if (!isFullScreen) {
      setControlsVisible(true);
      return undefined;
    }
    const resetTimer = () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(
        () => setControlsVisible(false),
        HIDE_DELAY_MS,
      );
    };
    const handleActivity = () => {
      setControlsVisible(true);
      resetTimer();
    };
    resetTimer();
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [isFullScreen]);

  useEffect(() => {
    progressRef.current = 0;
  }, [currentContent]);

  useEffect(() => {
    if (!autoAdvance) {
      progressRef.current = 0;
      return undefined;
    }
    if (!isPlaying) return undefined;
    const duration = getAutoplayDuration(adaptContentType(currentContent));
    if (duration <= 0) return undefined;

    const interval = 100;
    let elapsed = (progressRef.current / 100) * duration;
    const id = setInterval(() => {
      elapsed += interval;
      const progress = Math.min((elapsed / duration) * 100, 100);
      progressRef.current = progress;
      if (progress >= 100) {
        clearInterval(id);
        progressRef.current = 0;
        nextContent();
      }
    }, interval);
    return () => clearInterval(id);
  }, [currentContent, autoAdvance, isPlaying]);

  return (
    <div
      className={classnames(styles.controlsWrapper, {
        [styles.fullScreenWrapper]: isFullScreen,
      })}
    >
      <div
        className={classnames(styles.controls, {
          [styles.hidden]: isFullScreen && !controlsVisible,
        })}
      >
        {/* Keeps the navigation centered. */}
        <div className={styles.side} />

        <div className={styles.navPill}>
          <button
            type="button"
            className={styles.circleButton}
            onClick={prevContent}
            disabled={isFirst}
            aria-label="Previous memory"
          >
            <ArrowDownIcon className={styles.arrowLeft} />
          </button>
          <span className={styles.position}>
            {currentIndex + 1} of {allContents.length}
          </span>
          <button
            type="button"
            className={styles.circleButton}
            onClick={nextContent}
            disabled={isLast}
            aria-label="Next memory"
          >
            <ArrowDownIcon className={styles.arrowRight} />
          </button>
        </div>

        <div className={classnames(styles.side, styles.sideRight)}>
          {!hideAutoAdvance && (
            <div className={styles.autoAdvancePill} ref={menuRef}>
              <button
                type="button"
                role="switch"
                aria-checked={autoAdvance}
                className={styles.autoAdvanceButton}
                onClick={toggleAutoAdvance}
              >
                <span
                  className={classnames(styles.toggle, {
                    [styles.toggleOn]: autoAdvance,
                  })}
                >
                  <span className={styles.toggleKnob} />
                </span>
                <span className={styles.autoAdvanceLabel}>Auto-advance</span>
              </button>
              {showAutoAdvanceSettings && (
                // Opens upward and to the left: the pill sits at the right
                // edge of the center column, which clips overflow.
                <Menu
                  position={PositionType.TopLeft}
                  className={styles.settingsMenu}
                >
                  <div className={styles.settingsMenuHeader}>
                    <span className="text__body__semi__bold__small__gray800">
                      Auto-advance Settings
                    </span>
                    <span className="text__body__regular__tiny__gray600">
                      Select which content types to include when auto-advancing.
                    </span>
                  </div>
                  {Object.values(AlbumContentType).map((type) => (
                    <DropDownListItem
                      key={type}
                      checkboxItem={{
                        isChecked: autoAdvanceCheckedOptions[type],
                        onChange: () =>
                          handleAutoAdvanceCheckedOptionChange(type),
                        itemKey: type,
                      }}
                    >
                      {type.charAt(0).toUpperCase() +
                        type.slice(1).replace('_', ' ')}
                    </DropDownListItem>
                  ))}
                </Menu>
              )}
            </div>
          )}
          <button
            type="button"
            className={styles.fullscreenPill}
            onClick={() => setIsFullScreen(!isFullScreen)}
            aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
          >
            {isFullScreen ? <MinimizeIcon /> : <Expand05Icon />}
          </button>
        </div>
      </div>
    </div>
  );
};
